// Overflow-aware prev/next state for the Product Hotspot Recs carousel.
//
// A near-copy of product-family.js scoped to .product-hotspot-recs-slideshow, so the shared
// product-family behavior stays untouched. Disabled state comes from real scroll position
// rather than the slideshow's visibleSlides count, which can go stale and disable both arrows.

/**
 * Set a button's disabled state only when it differs, so re-asserting our state
 * doesn't create redundant mutations (which would loop the disabled observer).
 * @param {HTMLButtonElement} btn
 * @param {boolean} value
 */
function setDisabled(btn, value) {
  if (btn.disabled !== value) btn.disabled = value;
}

/** @param {HTMLElement} slideshow */
function syncButtons(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (!scroller || !nextBtn || !prevBtn) return;

  setDisabled(prevBtn, scroller.scrollLeft < 1);
  setDisabled(nextBtn, scroller.scrollLeft + scroller.offsetWidth >= scroller.scrollWidth - 1);
  syncArrowVisibility(slideshow);
}

/** @param {HTMLElement} slideshow */
function syncArrowVisibility(slideshow) {
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (!nextBtn || !prevBtn) return;
  slideshow.classList.toggle('both-arrows-disabled', prevBtn.disabled && nextBtn.disabled);
}

/** @param {HTMLElement} slideshow */
function updateOverflow(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
  if (!scroller) return;
  const overflows = scroller.scrollWidth > scroller.offsetWidth;
  slideshow.classList.toggle('has-overflow', overflows);
  if (overflows) {
    syncButtons(slideshow);
  } else {
    slideshow.classList.remove('both-arrows-disabled');
  }
}

/** @param {HTMLElement} slideshow */
function initSlideshow(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));

  updateOverflow(slideshow);

  if (scroller) {
    scroller.addEventListener(
      'scroll',
      () => {
        if (slideshow.classList.contains('has-overflow')) syncButtons(slideshow);
      },
      { passive: true },
    );
  }

  new ResizeObserver(() => updateOverflow(slideshow)).observe(slideshow);

  let pendingRecompute = false;
  new MutationObserver(() => {
    if (pendingRecompute) return;
    pendingRecompute = true;
    requestAnimationFrame(() => {
      pendingRecompute = false;
      updateOverflow(slideshow);
    });
  }).observe(slideshow, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // The native slideshow (assets/slideshow.js) derives arrow disabled state from its
  // center-based `current` index, which flags the prev arrow as enabled at scrollLeft 0
  // (our cards are narrow, so the centered slide is index 1). It re-runs that logic on
  // ResizeObserver triggers — e.g. when the mini-PDP drawer's scroll-lock changes the
  // body width — clobbering our scroll-based state. Re-assert ours whenever it does.
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (nextBtn && prevBtn) {
    let reasserting = false;
    const disabledObserver = new MutationObserver(() => {
      if (reasserting) return;
      if (!slideshow.classList.contains('has-overflow')) return;
      reasserting = true;
      syncButtons(slideshow);
      reasserting = false;
    });
    disabledObserver.observe(prevBtn, { attributes: true, attributeFilter: ['disabled'] });
    disabledObserver.observe(nextBtn, { attributes: true, attributeFilter: ['disabled'] });
  }
}

const CARD_SELECTOR = '.product-hotspot-recs product-card';
const QUICK_ADD_SELECTOR = '.product-hotspot-recs__quick-add';

/**
 * Cards in this row open the mini-PDP instead of navigating to the product page.
 *
 * @param {MouseEvent} event
 */
function handleCardClick(event) {
  // Leave new-tab / new-window / download intents and non-primary clicks to the browser.
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target instanceof Element ? event.target : null;
  // The "+" keeps its own binding — and this is what stops the forwarded click below recursing.
  if (!target || target.closest(QUICK_ADD_SELECTOR)) return;

  const quickAdd = target.closest(CARD_SELECTOR)?.querySelector(QUICK_ADD_SELECTOR);
  if (!(quickAdd instanceof HTMLElement)) return;

  event.preventDefault();
  quickAdd.click();
}

function init() {
  document.querySelectorAll('.product-hotspot-recs-slideshow').forEach(initSlideshow);
  document.addEventListener('click', handleCardClick, { capture: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
